/**
 * Этапы проекта и требования готовности.
 *
 * Готовность показывается объективным счётом «выполнено из всего»;
 * произвольный процент не используется. Пропущенные требования выводятся
 * отдельно и в выполненные не входят (документ «Итоговое описание», §16.2).
 *
 * Переход к следующему этапу выполняет человек: система показывает, что
 * осталось невыполненным, но не запрещает осознанный переход.
 */

import { FlagIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { ProjectStageDto, StageRequirementDto, StageStatus } from '@project-os/contracts';
import { STAGE_STATUS_LABELS_RU } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useCreateStage, useDashboard, useMakeStageCurrent, useStages } from '@/api/stages';
import { useProjectMembers } from '@/api/members';
import { useWorkstreams } from '@/api/workstreams';
import { Progress } from '@/components/common/progress';
import { StatusBadge } from '@/components/common/status-badge';
import { EmptyState, ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { formatDate, pluralRu } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useProjectContext } from '../project/use-project-context';
import { RequirementList } from './RequirementList';
import { ProjectTimeline } from '../timeline/ProjectTimeline';
import { ItemDetailPanel } from '../items/ItemDetailPanel';

const STAGE_TONES: Record<StageStatus, 'neutral' | 'progress' | 'success'> = {
  UPCOMING: 'neutral',
  CURRENT: 'progress',
  COMPLETED: 'success',
  SKIPPED: 'neutral',
};

export function StagesPage() {
  const project = useProjectContext();
  const { data: stages, isPending, isError, error, refetch } = useStages(project.id);
  const [createOpen, setCreateOpen] = useState(false);
  const [unfinished, setUnfinished] = useState<StageRequirementDto[] | null>(null);

  // Таймлайн собирает сервер вместе с обзором: считать полосы этапов
  // вторым способом значило бы получить два расходящихся результата.
  const timeline = useDashboard(project.id).data?.timeline;
  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  /** Переход с полосы этапа к его карточке ниже по странице. */
  const scrollToStage = (stageId: string) => {
    document.getElementById(`stage-${stageId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const canEdit = project.permissions.includes('STAGE_WRITE');

  // Текущий этап отделяется от остальных: он занимает всю ширину, прочие
  // встают в две колонки.
  const current = stages?.find((stage) => stage.status === 'CURRENT') ?? null;
  const others = (stages ?? []).filter((stage) => stage.id !== current?.id);

  return (
    <>
      <PageHeader
        title="Этапы"
        description="Что проект должен выполнить, чтобы двигаться дальше"
        actions={
          canEdit ? (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <PlusIcon />
              Новый этап
            </Button>
          ) : null
        }
      />

      {/* Таймлайн — на всю ширину полотна: карточки этапов читаются в
          колонке, а полосы дат — нет. */}
      {timeline && stages && stages.length > 0 ? (
        <div className="fade-in border-b border-border/40 px-6 pt-1 pb-4">
          <ProjectTimeline
            projectId={project.id}
            data={timeline}
            canEdit={canEdit}
            onOpenItem={(itemId) => setOpenItemId(itemId)}
            onOpenStage={scrollToStage}
          />
        </div>
      ) : null}

      {/* Полотно целиком: карточка этапа с требованиями — широкая таблица,
          а не колонка текста. Ограничение по ширине оставляло справа пустоту
          в треть экрана. */}
      <PageBody className="space-y-4">
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : stages.length === 0 ? (
          <EmptyState
            icon={<FlagIcon />}
            title="Этапов пока нет"
            description="Этап — это набор измеримых требований, без которых нет смысла двигаться дальше. Например: «Проверка проблемы» с требованиями «Определён первый сегмент» и «Проведены интервью»."
            action={
              canEdit ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <PlusIcon />
                  Создать первый этап
                </Button>
              ) : null
            }
            hint="Готовность считается счётом «выполнено из всего», а не процентом: процент без явных требований был бы выдуманным числом."
          />
        ) : (
          <>
            {/* Текущий этап во всю ширину: у него больше всего требований и
                именно он нужен каждый день. Остальные — в две колонки:
                они читаются как справка, а не как рабочий список. */}
            {current ? (
              <StageCard
                projectId={project.id}
                stage={current}
                canEdit={canEdit}
                onTransition={setUnfinished}
              />
            ) : null}

            {others.length > 0 ? (
              // Две колонки — только когда есть чем занять обе. Один
              // оставшийся этап в двухколоночной сетке оставляет пустой
              // правую половину экрана, а именно от этого мы и уходили.
              <div className={cn('grid items-start gap-4', others.length > 1 && '2xl:grid-cols-2')}>
                {others.map((stage) => (
                  <StageCard
                    key={stage.id}
                    projectId={project.id}
                    stage={stage}
                    canEdit={canEdit}
                    onTransition={setUnfinished}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </PageBody>

      <CreateStageDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectId={project.id}
        hasCurrent={Boolean(stages?.some((stage) => stage.status === 'CURRENT'))}
      />

      <UnfinishedDialog requirements={unfinished} onClose={() => setUnfinished(null)} />

      {/* Точка на таймлайне открывает карточку здесь же: уходить на страницу
          элементов ради одного срока значит терять контекст этапов. */}
      <ItemDetailPanel
        projectId={project.id}
        itemId={openItemId}
        onClose={() => setOpenItemId(null)}
        onOpenItem={setOpenItemId}
        canEdit={canEdit}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />
    </>
  );
}

function StageCard({
  projectId,
  stage,
  canEdit,
  onTransition,
}: {
  projectId: string;
  stage: ProjectStageDto;
  canEdit: boolean;
  onTransition: (requirements: StageRequirementDto[]) => void;
}) {
  const makeCurrent = useMakeStageCurrent(projectId);
  const isCurrent = stage.status === 'CURRENT';

  const handleMakeCurrent = async () => {
    try {
      const result = await makeCurrent.mutateAsync(stage.id);
      toast.success(`Текущий этап: ${stage.name}`);
      if (result.unfinishedRequirements.length > 0) {
        onTransition(result.unfinishedRequirements);
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось перейти к этапу');
    }
  };

  return (
    // id — якорь для перехода с таймлайна.
    <Card id={`stage-${stage.id}`} className={cn('scroll-mt-4', isCurrent && 'border-primary/50')}>
      <CardHeader className="gap-3 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold">{stage.name}</h2>
              <StatusBadge
                label={STAGE_STATUS_LABELS_RU[stage.status]}
                tone={STAGE_TONES[stage.status]}
              />
            </div>
            {stage.descriptionMd ? (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {stage.descriptionMd}
              </p>
            ) : null}
          </div>

          {canEdit && !isCurrent && stage.status !== 'COMPLETED' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={makeCurrent.isPending}
              onClick={() => void handleMakeCurrent()}
            >
              {makeCurrent.isPending ? <Loader2Icon className="animate-spin" /> : <FlagIcon />}
              Сделать текущим
            </Button>
          ) : null}
        </div>

        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
            <span className="font-medium text-foreground tabular-nums">
              {stage.readiness.total > 0
                ? `${stage.readiness.done} из ${stage.readiness.total} требований выполнено`
                : 'Требования не заданы'}
            </span>
            <span className="text-muted-foreground">
              {stage.readiness.skipped > 0
                ? `${pluralRu(stage.readiness.skipped, ['пропущено', 'пропущено', 'пропущено'])} `
                : null}
              {stage.readiness.blocked > 0 ? (
                <span className="text-tone-danger">
                  {pluralRu(stage.readiness.blocked, [
                    'заблокировано',
                    'заблокировано',
                    'заблокировано',
                  ])}
                </span>
              ) : null}
              {stage.targetDate ? ` · до ${formatDate(stage.targetDate)}` : null}
            </span>
          </div>
          <Progress
            className="mt-2"
            value={stage.readiness.done}
            max={stage.readiness.total}
            label={`Выполнено ${stage.readiness.done} из ${stage.readiness.total}`}
          />
        </div>
      </CardHeader>

      <CardContent>
        <RequirementList
          projectId={projectId}
          stage={stage}
          canEdit={canEdit}
          defaultOpen={isCurrent}
        />
      </CardContent>
    </Card>
  );
}

function CreateStageDialog({
  open,
  onOpenChange,
  projectId,
  hasCurrent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  hasCurrent: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [makeCurrent, setMakeCurrent] = useState(!hasCurrent);
  const create = useCreateStage(projectId);

  const submit = async () => {
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        descriptionMd: description.trim() || null,
        targetDate: targetDate || null,
        makeCurrent,
      });
      toast.success('Этап создан');
      setName('');
      setDescription('');
      setTargetDate('');
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось создать этап');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новый этап</DialogTitle>
          <DialogDescription>
            Требования добавляются после создания. Готовность считается по ним, а не по субъективной
            оценке.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="stage-name">Название</Label>
            <Input
              id="stage-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Например: Проверка проблемы"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stage-description">Описание</Label>
            <Textarea
              id="stage-description"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Чего проект должен добиться на этом этапе"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stage-target">Целевая дата</Label>
            <Input
              id="stage-target"
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-3.5 accent-primary"
              checked={makeCurrent}
              onChange={(event) => setMakeCurrent(event.target.checked)}
            />
            Сделать текущим этапом
            {hasCurrent ? (
              <span className="text-xs text-muted-foreground">— прежний будет завершён</span>
            ) : null}
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button disabled={!name.trim() || create.isPending} onClick={() => void submit()}>
            {create.isPending ? <Loader2Icon className="animate-spin" /> : null}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Предупреждение о невыполненных требованиях прежнего этапа. */
function UnfinishedDialog({
  requirements,
  onClose,
}: {
  requirements: StageRequirementDto[] | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(requirements)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Переход выполнен</DialogTitle>
          <DialogDescription>
            На прежнем этапе осталось невыполненное. Это не ошибка — переход был осознанным, но
            список стоит помнить.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1.5">
          {(requirements ?? []).map((requirement) => (
            <li key={requirement.id} className="flex gap-2 text-sm">
              <span aria-hidden className="text-muted-foreground">
                ○
              </span>
              {requirement.title}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button onClick={onClose}>Понятно</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
